// © Microsoft Corporation. All rights reserved.

import { canUseDOM, getDocumentElementsByTagName, getLocationSearch, getLocationHostname } from "@msnews/isomorphic-util";
import { applicationCacheConstants, clientSettings, dir, htmlElementIds, info, js, lang, pagetypeSettings, CoreLocalStorageKeys } from "../constants";

import { IClientSettings } from "./ClientSettings";
import { AudienceModeType, BrowserType } from "@msnews/experiences-constants";
import { WebClientData } from "../types/WebClientData";
import { UrlUtility } from "../utilities/UrlUtility";
import { getLocalStorage } from "../webstorage";
import { tryParseJson } from "../utilities/Parsing";
import { KeyValueArray } from "../utilities";
import { logger, updateAppErrorsPropertyBag } from "@msnews/diagnostics";
import { parseISO } from "date-fns";
import { getStoredHeadData, setStoredHeadData, HeadDataResponse } from "./StoredHeadData";
import { eNtpChromiumVersion } from "./Enterprise";

/**
 * Format used for activity ID stored in storage
 */
export type StoredActivityId = {
    /**
     * ID value
     */
    id: string;

    /**
     * Timestamp of when value was stored
     */
    ts: number;
};

export const MmxWwwContentFlightId = "prg-mmxcontent-t";

/** Type for storing app versions for different page types */
export type PageAppVersions = { [pageType: string]: string };

/**
 * Class for getting the head data information.
 */
class HeadData {
    /**
     * The single instance of the HeadData class.
     * @type {HeadData}
     */
    private static _instance: HeadData;

    private anonCookieName: string;
    private locale: string;
    private marketDir: string;
    private currentFlights: string;
    private clientSettings: IClientSettings;
    private enableCMSPPEConfigs = false;
    private userIsSignedIn: boolean; // Always signed out on server side: https://dev.azure.com/microsoft/OS/_workitems/edit/25344027
    private isPreload: boolean;
    private isPrerender: boolean;
    private headElement: any;
    private htmlElement: any;
    private applicationCache: any;
    private isAppcache: boolean;
    private isBlendedEnterprise: boolean;
    private isMmxWwwContentRequest: boolean;
    private isServiceWorkerCache: boolean;
    private ssoComplete: boolean;
    private aadState: boolean;
    private locationSearch: string;
    private lastKnownAppVersion: string;

    // TODO: Task 24890741: refactor this class to use the current singleton pattern with ES6 modules
    // This will also clean up these getters/setters
    public get Locale(): string { return this.locale; }

    public get MarketDir(): string { return this.marketDir; }
    public set MarketDir(value: string) { this.marketDir = value; }

    public get CurrentFlights(): string { return this.currentFlights || ""; }
    public set CurrentFlights(value: string) { this.currentFlights = value; }

    public get ClientSettings(): IClientSettings { return this.clientSettings; }
    public set ClientSettings(value: IClientSettings) { this.clientSettings = value; }

    public get EnableCMSPPEConfigs(): boolean { return this.enableCMSPPEConfigs; }

    public get UserIsSignedIn(): boolean { return this.userIsSignedIn; }
    public set UserIsSignedIn(value: boolean) { this.userIsSignedIn = value; }

    public get IsPreload(): boolean { return this.isPreload; }
    public set IsPreload(value: boolean) { this.isPreload = value; }

    public get IsPrerender(): boolean { return this.isPrerender; }
    public set IsPrerender(value: boolean) { this.isPrerender = value; }

    public get IsAppCache(): boolean { return this.isAppcache; }

    public get IsBlendedEnterprise(): boolean { return this.isBlendedEnterprise; }
    public set IsBlendedEnterprise(value: boolean) { this.isBlendedEnterprise = value; }

    public get IsMmxWwwContentRequest(): boolean { return this.isMmxWwwContentRequest; }
    public set IsMmxWwwContentRequest(value: boolean) { this.isMmxWwwContentRequest = value; }

    public get IsServiceWorkerCache(): boolean { return this.isServiceWorkerCache; }

    /**
     * The last known app version for the currently loaded page type.
     * To get the current page version, use clientSettings.bundleInfo.v value.
     */
    public get LastKnownAppVersion(): string { return this.lastKnownAppVersion; }

    // TODO: Dependencies that some client side code expects headData to specify but doesn't due to PCS
    public get AnonCookieName(): string { return this.anonCookieName; }
    public set AnonCookieName(value: string) { this.anonCookieName = value; }

    public get SsoComplete(): boolean { return this.ssoComplete; }

    public get AadState(): boolean { return this.aadState; }
    public set AadState(value: boolean) { this.aadState = value; }

    public set LocaleName(value: string) { this.locale = value; }

    /**
     * True if we need to generate a new RID for telemetry.
     * In normal scenarios the ActivityID and telemetry RID will match. But sometimes we are unable to refresh the head data
     * to get a new activity ID. In those cases we need to generate a fresh RID for telemetry, and the ActivityID and RID will
     * not match.
     */
    public newRidNeeded: boolean;

    /**
     * Initialize the head data instance, on server side provide the client info & client settings
     * @param {WebClientData} clientData Data object containing both client info and settings
     */
    private constructor(clientData?: WebClientData) {
        this.initializeHeadData(clientData);
    }

    /**
     * Returns singleton class instance.
     * @returns {HeadData}
     */
    public static getInstance(): HeadData { return HeadData._instance || (HeadData._instance = new HeadData()); }

    /**
     * Reset singleton class instance with passed in info and client settings. Used on server side to reset HeadData for each request.
     * @param {WebClientData} clientData Data object containing both client info and settings
     * @returns {HeadData}
     */
    public static resetInstance(clientData?: WebClientData): HeadData {
        HeadData._instance = new HeadData(clientData);
        headData = HeadData._instance;
        return HeadData._instance;
    }

    /**
     * Initializes head data. If WebClientData is provided use that, otherwise do client side initialization.
     * @param {WebClientData} clientData - Data object containing both client info and settings
     */
    public initializeHeadData(clientData?: WebClientData): void {
        if (clientData) {
            // Data is passed in only from Render Service under Node.JS environment since there is no DOM.
            this.initializeFromData(clientData);
            return;
        }

        this.newRidNeeded = undefined;
        this.lastUsedActivityIdBacking = undefined;
        this.locationSearch = getLocationSearch();
        this.htmlElement = canUseDOM() ? getDocumentElementsByTagName(htmlElementIds.html)[0] : null;
        this.headElement = canUseDOM() ? getDocumentElementsByTagName(htmlElementIds.head)[0] : null;
        this.applicationCache = canUseDOM() ? window.applicationCache : null;

        if (!this.htmlElement || !this.headElement) {
            return;
        }

        // We always initialize from DOM first because
        // even if we are in an appcache/service worker case, we still need some data from the DOM
        // and we will fallback to DOM if there are no items found in storage
        this.initializeFromDom();

        // Attempt to override with local storage item when in appcache scenario
        // In service worker scenario, just update the storage activity id
        if (this.applicationCache && this.htmlElement.getAttribute(applicationCacheConstants.manifestAttributeName)) {
            this.isAppcache = true;
        } else if (window && window.navigator && window.navigator.serviceWorker && window.navigator.serviceWorker.controller) {
            this.isServiceWorkerCache = true;
        }

        if (this.isAppcache || this.isServiceWorkerCache) {
            // First apply head data from local storage. This might change the AID
            this.initializeFromStorage();

            // Now update last used activity ID based on the current activity ID
            this.initializeLastUsedActivityId();
        }

        // Below items are common for both scenarios
        const clientSettingsLocaleExists = this.clientSettings && this.clientSettings.locale && this.clientSettings.locale.language && this.clientSettings.locale.market;
        this.locale = clientSettingsLocaleExists &&
            (
                this.clientSettings.locale.language
                + "-"
                + (this.clientSettings.locale.script ? this.clientSettings.locale.script + "-" : "")
                + this.clientSettings.locale.market
            ).toLowerCase() || "";

        const marketDir = this.htmlElement.getAttribute(dir);
        this.marketDir = marketDir && marketDir.toLocaleLowerCase() || "";

        this.setQueryItemProvidedValues();

        // Note: This isn't currently being stamped by PCS
        const headDataJs = this.headElement.getAttribute(js);
        if (headDataJs) {
            const headJs = <{ anonckname: string, signedin: number }>tryParseJson(headDataJs);
            this.anonCookieName = headJs.anonckname;
            this.userIsSignedIn = headJs.signedin === 1;
        }

        // Checks if current request is background preload
        if (UrlUtility.getQueryParameterByName("backgroundpreload", this.locationSearch)
            && document.visibilityState === "hidden") {
            this.isPreload = true;
            this.logDocumentVisibility();
        }

        // Checks if current request is prerender request
        if (UrlUtility.getQueryParameterByName("prerender", this.locationSearch)
            && document.visibilityState === "hidden") {
            this.isPrerender = true;
            this.logDocumentVisibility();
        }

        // Determine if we are in Enterprise version or not
        this.setIsBlendedEnterprise();
        this.setIsMmxWwwContentRequest();
    }

    private lastUsedActivityIdBacking: StoredActivityId;

    /**
     * Gets the last used activity ID, or null if there is none stored.
     */
    public get lastUsedActivityId(): StoredActivityId {
        if (this.lastUsedActivityIdBacking) {
            return this.lastUsedActivityIdBacking;
        }

        const storageKey: string = this.getStoredActivityIdKey();
        const storedActivityIdJson: string = getLocalStorage().getItem(storageKey);
        if (storedActivityIdJson) {
            this.lastUsedActivityIdBacking = tryParseJson(storedActivityIdJson);
        }

        return this.lastUsedActivityIdBacking;
    }

    /**
     * Sets the last used activity ID. Pass in null to clear the stored data.
     */
    public set lastUsedActivityId(activityId: StoredActivityId) {
        const storageKey: string = this.getStoredActivityIdKey();
        if (activityId) {
            if (activityId.id && activityId.ts) {
                getLocalStorage().setItem(storageKey, JSON.stringify(activityId));
                this.lastUsedActivityIdBacking = activityId;
            }
        } else {
            getLocalStorage().removeItem(storageKey);
        }
    }

    /**
     * Gets the stored head data, or null if none is stored.
     */
    public get storedHeadData(): HeadDataResponse {
        return getStoredHeadData(this.clientSettings.pagetype);
    }

    /**
     * Sets the stored head data. Pass in null to clear the stored data.
     */
    public set storedHeadData(headDataResponse: HeadDataResponse) {
        setStoredHeadData(this.clientSettings.pagetype, headDataResponse);
    }

    /**
     * Initializes the client settings and info from client data passed in externally (render service query param)
     * @param {WebClientData} clientData Data object containing both client info and settings
     */
    private initializeFromData(clientData: WebClientData): void {
        this.clientSettings = clientData.clientSettings;

        this.setFlights(clientData.clientInfo);

        this.setQueryItemProvidedValues();

        const clientSettingsLocaleExists = this.clientSettings && this.clientSettings.locale && this.clientSettings.locale.language && this.clientSettings.locale.market;
        this.locale = clientSettingsLocaleExists &&
            (
                this.clientSettings.locale.language
                + "-"
                + (this.clientSettings.locale.script ? this.clientSettings.locale.script + "-" : "")
                + this.clientSettings.locale.market
            ).toLowerCase() || "";
        this.marketDir = clientData.marketDir && clientData.marketDir.toLocaleLowerCase() || "";

        // Determine if we are in Enterprise version or not
        this.setIsBlendedEnterprise();
        this.setIsMmxWwwContentRequest();
    }

    /**
     * Initializes the client settings and info from DOM element
     */
    private initializeFromDom(): void {

        this.setFlights(this.headElement.getAttribute(info));

        const dataClientSettings = this.headElement.getAttribute(clientSettings);
        if (!dataClientSettings) {
            return;
        }

        this.clientSettings = <IClientSettings>tryParseJson(dataClientSettings);
        if (!this.clientSettings) {
            return;
        }

        this.calculateForwardableParams();
        const { pagetype } = this.clientSettings;

        // If clientSettings.pagetype does not exist, try getting from data-pagetype.
        // For Peregrine full spa page: pagetype is part of data-client-settings. There is no data-pagetype.
        // For Prime/Hybrid, pagetype is outside of data-client-settings and it belongs to data-pagetype
        if (!pagetype) {
            const pageTypeFromSettings = this.headElement.getAttribute(pagetypeSettings);
            this.clientSettings.pagetype = pageTypeFromSettings == null ? undefined : pageTypeFromSettings;
        }

        // try to get the previously store page version from local storage for this page type,
        // and store back the current version
        if (pagetype) {
            try {
                const storedVersions = getLocalStorage().getObject<PageAppVersions>(CoreLocalStorageKeys.pageVersions) || {};
                this.lastKnownAppVersion = storedVersions[pagetype];

                // update the dictionary with the new value if different, and store it back to local storage
                const { bundleInfo: currentDomVersion } = this.clientSettings;
                if (currentDomVersion && this.lastKnownAppVersion !== currentDomVersion.v) {
                    storedVersions[pagetype] = currentDomVersion.v;
                    getLocalStorage().setObject(CoreLocalStorageKeys.pageVersions, storedVersions);
                }
            } catch {
                logger.log(`${this.logPrefix()} couldn't complete local storage related operation.`);
            }
        }
    }

    /**
     * Initializes headData client settings and info from the value found in storage
     * and if none is found, falls back to using the DOM element
     */
    private initializeFromStorage(): void {
        const currentPageStoredHeadData = this.storedHeadData;

        if (currentPageStoredHeadData) {
            this.populateFromApiResponse(currentPageStoredHeadData);
        }
    }

    /**
     * Populates fields based on a HeadData API response.
     * @param response The response to populate from.
     */
    private populateFromApiResponse(response: HeadDataResponse) {
        if (!response) {
            return;
        }

        const responseClientSettings = response.clientSettings;
        const responseFlights = response.info;

        // The stored head data is corrupt, bail
        if (!responseClientSettings || !responseFlights) {
            return;
        }

        // If DOM page gen time is later than response page gen time, bail. We don't want to load stale head data from storage.
        if (this.clientSettings.pageGenTime >= responseClientSettings.pageGenTime) {
            // Purge obsolete stored head data.
            this.storedHeadData = null;
            return;
        }

        // Hold on to the current DOM queryparams as we will need to override
        // what we get from what is in storage
        const domQueryParams = this.clientSettings.queryparams;

        // save the current version if available in a temp variable so we can
        // restore it back after updating the clientSettings object with the one from
        // storage (which came from head data API, which does not contain the version info
        // by design, because version is tied with the static bundles, which should represent the currently
        // loaded page.
        const currentPageVersion = this.clientSettings && this.clientSettings.bundleInfo && this.clientSettings.bundleInfo.v;
        this.clientSettings = responseClientSettings;

        // restore the version after the object update
        this.clientSettings.bundleInfo = Object.assign(this.clientSettings.bundleInfo || {}, { v: currentPageVersion });

        // restore the queryParams base on what is found in DOM
        // as we want the DOM value to be what is used
        // and not what is found in storage for this field
        // We want these to stay true to what PCS generated for the page
        // and avoid desync when reading what comes from future headData API calls.
        this.clientSettings.queryparams = domQueryParams;
        this.calculateForwardableParams();

        this.setFlights(responseFlights);
    }

    /**
     * Initializes the queryparams from parameters
     */
    private calculateForwardableParams() {
        const params = this.clientSettings ? this.clientSettings.queryparams : "";
        if (!params) {
            return;
        }
        const forwardableParams = ["fdhead", "item"];
        const lowerCaseParams = params.toLocaleLowerCase();
        const availableParams = forwardableParams.map((key) => {
            const paramValue = UrlUtility.getQueryParameterByName(key, lowerCaseParams);
            if (!paramValue) {
                return null;
            }
            return `${key}=${encodeURIComponent(paramValue)}`;
        }).filter(x => x);
        this.clientSettings.forwardablequeryparams = availableParams.join("&");
    }

    /**
     * Initializes the "newRidNeeded" value based on the page generation time and last used activity ID timestamp.
     * Updates the stored "last used activity ID"
     */
    private initializeLastUsedActivityId() {
        // Read the "last used activity ID"
        const lastUsedAid = this.lastUsedActivityId;

        // The activity ID that the page is currently planning on using. This may have been embedded or it may have been loaded up from stored head data.
        const currentAid = this.clientSettings.aid;
        const pageGenTimeDate: Date = parseISO(this.clientSettings.pageGenTime);
        const pageGenTimestamp: number = pageGenTimeDate.getTime();

        // Three conditions need to be met to generate a new RID
        // 1) The user has stored a previously used activity ID
        // 2) We have a valid page generation timestamp from PCS. PCS embeds pageGenTimestamp in the client settings to tell us
        //    when it ran and generated the page. This will not be the case for localhost requests because they do not use PCS and instead
        //    use a static App.html file. For localhost we do not need to generate a new RID.
        // 3) The current ActivityId matches the previously used one
        this.newRidNeeded = !!(lastUsedAid && pageGenTimestamp && lastUsedAid.id === currentAid);

        // If we have a proper timestamp and it's more recent than the last stored one
        if (pageGenTimestamp && (!lastUsedAid || pageGenTimestamp > lastUsedAid.ts)) {
            // Store the current activity ID as last used.
            this.lastUsedActivityId = { id: currentAid, ts: pageGenTimestamp };
        }
    }

    /**
     * Parses flights from provided string (if in proper format of f:{flights})
     * and sets it to the headData object
     */
    private setFlights(flightString: string): void {
        // The comma separated list of flights enabled in current request
        this.currentFlights = ((/f:\s*([^;]+)/i.exec(flightString) || {})[1] || "").toLowerCase();
    }

    /**
     * Determines if we are in the blended Enterprise version based on clientSettings and currentFlights
     */
    private setIsBlendedEnterprise(): void {
        // We know we are in the Enterprise version of the page when both the audience mode is set to Enterprise
        // & the browser is edgeChromium, version >= 87
        this.IsBlendedEnterprise = this.clientSettings &&
            this.clientSettings.audienceMode === AudienceModeType.Enterprise &&
            this.clientSettings.browser &&
            this.clientSettings.browser.browserType === BrowserType.EdgeChromium &&
            parseInt(this.clientSettings.browser.version as string) >= eNtpChromiumVersion;
    }

    /**
     * Determines if we are in Www content version based on clientSettings and currentFlights
     */
    private setIsMmxWwwContentRequest(): void {
        this.isMmxWwwContentRequest = this.currentFlights && this.currentFlights.includes(MmxWwwContentFlightId);
    }

    /**
     * Read query items to determine the headdata variable values.
     */
    private setQueryItemProvidedValues(): void {
        // This only finds multiples of item=key:value qsp. If keys are the same, first given will be used (similar to UrlSearchParamsHelper).
        const queryItems = UrlUtility.getParamsWithItems(getLocationSearch());
        const cmsItem = queryItems.find(item => item.key === "cms");
        this.enableCMSPPEConfigs = (cmsItem && cmsItem.value === "ppe");

        // Test override to add flights for scope purposes
        this.setCurrentFlightsTestOverride(queryItems);
    }

    /**
     * Overrides currentFlights when hostname is localhost. Used for testing.
     */
    private setCurrentFlightsTestOverride(queryItems: KeyValueArray<string, string>): void {
        if (getLocationHostname().toLowerCase().includes("localhost") && queryItems) {
            const flights = queryItems.find(item => item.key === "flights");
            if (flights) {
                this.currentFlights = flights.value;
            }
        }
    }

    /**
     * Gets the local storage key for the head data.
     */
    private getStoredActivityIdKey(): string {
        return "sai_" + this.clientSettings.pagetype;
    }

    /**
     * Prefix for headdata logs. Server side add the head and html elements.
     * @returns the prefix string for logger message.
     */
    private logPrefix(): string {
        // Request Id is only available on server side.
        return `HeadData:${(canUseDOM() ? "" : ` [Head:${this.headElement}] and [Html:${this.htmlElement}]`)}`;
    }

    /**
     * Updates AppError property bag with document visibility and adds event listener for visibility change
     */
    private logDocumentVisibility(): void {
        if (!canUseDOM()) {
            return;
        }

        updateAppErrorsPropertyBag({ isVisible: document.visibilityState === "visible" });

        document.addEventListener("visibilitychange", () => {
            updateAppErrorsPropertyBag({ isVisible: document.visibilityState === "visible" });
        });
    }
}

export const getHeadData = HeadData.getInstance;

// headData can be reinitialized through resetHeadData.
export let headData = HeadData.getInstance();

/**
 * Export function to reset headData based on WebClientData
 */
export const resetHeadData = HeadData.resetInstance;